"use client"

import {
  ChevronsUpDown,
  ExternalLink,
  HelpCircle,
  LogOut,
  MonitorDown,
  Settings,
  Sparkles,
} from "lucide-react"
import { useNavigate } from "react-router";

import { useUserActions, useUserInfo } from "@/modules/auth";
import { lockPrivacySession } from "@/modules/mine/privacy/session";
import { SettingsDialog } from "@/modules/settings";
import { useSubscriptionOverviewQuery } from "@/modules/subscription";
import logo from "@/shared/assets/images/logo.png";
import { useDialog } from "@/shared/components/dialog";
import { ROUTE_PATHS } from "@/shared/constants/route-paths";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/shared/ui/sidebar"
import { useFolderPrivacyDialog } from "@/widgets/privacy/use-folder-privacy-dialog";

const DOWNLOAD_URL = "https://help.withpinbox.com/how-to.html";
const HELP_URL = "https://help.withpinbox.com/";

function SidebarUser() {
  const navigate = useNavigate();
  const dialog = useDialog();
  const userInfo = useUserInfo();
  const { data: subscriptionOverview } = useSubscriptionOverviewQuery(userInfo?.id ?? "");
  const openPrivacyDialog = useFolderPrivacyDialog();
  const { logout: logoutUser } = useUserActions();
  const avatarUrl = userInfo?.qq?.avatar_url ?? userInfo?.wechat?.avatar_url ?? logo;
  const logout = async () => {
    const confirmed = await dialog.confirm({
      title: "退出登录",
      description: "确定要退出当前账号吗？退出后需要重新登录。",
      confirmText: "退出登录",
      closeOnOutside: false,
    });

    if (!confirmed) return;

    lockPrivacySession({ refetch: false });
    logoutUser();
    navigate(ROUTE_PATHS.LOGIN);
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={avatarUrl} alt={userInfo?.nickname} />
                <AvatarFallback className="rounded-lg">CN</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{userInfo?.nickname || '未设置昵称'}</span>
                {/* <span className="truncate text-xs">{userInfo?.email}</span> */}
                <span className="truncate text-xs text-muted-foreground">
                  {subscriptionOverview?.subscription?.plan_name ?? "套餐信息"}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="min-w-56"
            side="top"
            align="end"
            sideOffset={8}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={avatarUrl} alt={userInfo?.nickname} />
                  <AvatarFallback className="rounded-lg">CN</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{userInfo?.nickname || '未设置昵称'}</span>
                  <span className="truncate text-xs">{userInfo?.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => navigate(ROUTE_PATHS.PRICING)}>
                <Sparkles />
                升级套餐
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => {
                  void dialog.open(SettingsDialog, {
                    onOpenPricing: () => navigate(ROUTE_PATHS.PRICING),
                    onOpenEducation: () => navigate(ROUTE_PATHS.EDUCATION),
                    onUnlockPrivacy: async () => (await openPrivacyDialog("unlock")) === true,
                    onManagePrivacy: async () => (await openPrivacyDialog("manage")) === true,
                    onLockPrivacy: () => lockPrivacySession({ closeDialogs: false }),
                    onAccountDeleted: () => {
                      lockPrivacySession({ refetch: false });
                      logoutUser();
                      navigate(ROUTE_PATHS.LOGIN);
                    },
                  }, {
                    className:
                      "!h-[640px] !max-h-[calc(100vh-2rem)] !max-w-[880px] gap-0 overflow-hidden p-0",
                  });
                }}
              >
                <Settings />
                设置
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href={DOWNLOAD_URL} rel="noreferrer" target="_blank">
                  <MonitorDown />
                  下载 app 和扩展
                  <ExternalLink className="ml-auto size-3.5" />
                </a>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <a href={HELP_URL} rel="noreferrer" target="_blank">
                <HelpCircle />
                帮助
                <ExternalLink className="ml-auto size-3.5" />
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => void logout()}>
              <LogOut />
              退出登录
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

export default SidebarUser;
